import * as React from 'react';
import cx from 'classnames';

import { Flex } from '@common/components/Flex';
import { NewTableSortingDirection } from '@common/components/new-table/new-table.types';
import { CellProps, useController } from './cell.controller';
import { getTestAttributes } from '@common/utils/tests';

import './cell.styles.scss';

export const Cell: React.FC<CellProps> = (props) => {
  const {
    originalColumn,
    isDragging,
    withDragAndDrop,
    handleContextMenu,
    handleSort,
    initMouseUp,
    dragRef,
    dropRef,
    previewRef,
  } = useController(props);

  const { loadTableMetadata, loading, currentColumnResizeOffset, headerCell, getId, onColumnResizeEnd } = props;

  const filteredColumnsId = Object.keys(loadTableMetadata?.filtering?.columns || {});
  const isFiltered = filteredColumnsId?.some((id) => id === headerCell.id);
  const isSorted = loadTableMetadata?.sorting?.id === headerCell.id;
  const sortingDirection = loadTableMetadata?.sorting?.direction;
  const hasSortIcon = isSorted && sortingDirection;
  const resizable = (headerCell.column.columnDef.enableResizing ?? true);
  const content = typeof headerCell.column.columnDef.header === 'function'
    ? headerCell.column.columnDef.header(headerCell.getContext())
    : headerCell.column.columnDef.header;

  const cellSize = headerCell.getSize();
  const dragAndDropWidth = 12;

  const additionalElementsWidth = React.useMemo(() => {
    let value = resizable ? 20 : 0;

    if (withDragAndDrop) {
      value = value + dragAndDropWidth;
    }

    return value;
  }, [resizable, cellSize, withDragAndDrop]);
  let textWidth = cellSize - additionalElementsWidth;

  if (textWidth < 0) {
    textWidth = 15;
  }

  const anyLoading = loading.columns || loading.list;

  return (
    <th
      colSpan={headerCell.colSpan}
      style={{
        minWidth: cellSize,
        width: cellSize,
        maxWidth: cellSize,
        opacity: isDragging ? 0.5 : 1
      }}
      ref={dropRef}
      className={cx(
        'NewTable__header__cell',
        {
          'NewTable__header__cell--disabled': anyLoading,
          'NewTable__header__cell--sortable': headerCell.column.columnDef.enableSorting,
          'NewTable__header__cell--resizable': resizable,
          'NewTable__header__cell--draggable': originalColumn?.enableDragAndDrop,
        }
      )}
      title={anyLoading ? 'Please wait...' : content?.toString()}
      data-id={headerCell.id}
      {...getTestAttributes({ id: getId.root, })}
      onContextMenu={anyLoading ? undefined : handleContextMenu}
    >
      <Flex
        inline
        reference={previewRef}
        data-id={headerCell.id}
        alignItems="center"
        className="NewTable__header__cell__inner"
        style={{
          minWidth: textWidth,
          width: `calc(100% - ${additionalElementsWidth}px)`,
          maxWidth: `calc(100% - ${additionalElementsWidth}px)`,
        }}
        onClick={headerCell.column.columnDef.enableSorting ? handleSort : undefined}
      >
        {hasSortIcon && (
          <span
            className={cx(
              'fa',
              sortingDirection === NewTableSortingDirection.asc ? 'fa-caret-up' : 'fa-caret-down',
              'NewTable__header__cell__sort-icon'
            )}
          />
        )}
        <div
          style={{
            width: `calc(100% - ${hasSortIcon ? '8px' : '0px'} - ${isFiltered ? '8px' : '0px'}})`
          }}
          className="NewTable__header__cell__text"
        >
          {content}
        </div>
        {isFiltered && <span className="fa fa-filter NewTable__header__cell__filter-icon" />}
      </Flex>
      {dragRef && (
        <button 
          ref={dragRef}
          style={{
            cursor: isDragging ? 'grabbing' : 'grab',
            width: dragAndDropWidth
          }}
          className="NewTable__header__cell__drag-btn"
        >
          <i className="fa fa-arrows" />
        </button>
      )}
      {resizable && (
        <span
          onMouseDown={(e) => { // TODO think of better solution
            if (onColumnResizeEnd) {
              initMouseUp();
            }

            e.stopPropagation();
            const handle = headerCell.getResizeHandler();
            handle(e);
          }}
          className={cx(
            'fa',
            'fa-ellipsis-v',
            'NewTable__header__cell__resize-icon',
            { 'NewTable__header__cell__resize-icon--active': headerCell.column.getIsResizing() },
          )}
          style={{
            transform: headerCell.column.getIsResizing()
              ? `translateX(${currentColumnResizeOffset}px)`
              : '',
          }}
        />
      )}
    </th>
  );
};